import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import { fetchWithAuth } from '../utils/auth';
import { getWsApiBaseUrl } from '../config/api';

type Detail = {
  appeal: any;
  decision: any;
  violation: any;
  history: any[];
};

const ZERO_CODES = new Set(['MINOR', 'TERROR']);

/** Codes whose most common false positive is worth naming to the reviewer. */
const FALSE_POSITIVE_HINT: Record<string, string> = {
  NSFW: 'Traditional or cultural dress is the most common false positive on § 2.1. Watch the flagged seconds before deciding.',
  MINOR: 'A young-looking adult is the most common false positive here. This code has fired 91 times and no human has ever checked one.',
  VIOLENCE: 'Televised sport and news footage are the most common false positives on § 2.3.',
  COPYRIGHT: 'Check whether the creator has attached a licence or receipt before upholding.',
  DUPLICATE: 'A series or a part 2 is not a duplicate. Compare the actual frames.',
};

const AppealDetail: React.FC = () => {
  const { appealId } = useParams<{ appealId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [outcome, setOutcome] = useState<'overturn' | 'uphold' | 'close'>('overturn');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(`${getWsApiBaseUrl()}/admin/enforcement/appeals/${appealId}`);
      const json = await res.json();
      if (!json?.status) throw new Error(json?.message || 'Failed to load');
      setData(json.data);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load appeal');
    } finally {
      setLoading(false);
    }
  }, [appealId]);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async () => {
    if (!reason.trim()) {
      toast.error('A reason is required — the creator sees it as the statement of reasons.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetchWithAuth(`${getWsApiBaseUrl()}/admin/enforcement/appeals/${appealId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          upheld: outcome === 'overturn',
          outcomeReason: reason.trim(),
          closeAccount: outcome === 'close',
        }),
      });
      const json = await res.json();
      if (!json?.status) throw new Error(json?.message || 'Failed to save');
      toast.success(outcome === 'overturn' ? 'Overturned — creator restored' : 'Decision recorded');
      navigate('/appeals');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to record decision');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return (
      <div className='min-h-screen bg-[#05060A]'>
        <Navbar />
        <div className='max-w-[1920px] mx-auto px-6 pt-10 text-sm text-gray-500'>Loading…</div>
      </div>
    );
  }

  const { appeal, decision, violation, history } = data;
  const neverHuman = !decision?.ActorUserID;
  const isZero = ZERO_CODES.has(decision?.Code || '');
  const hint = FALSE_POSITIVE_HINT[decision?.Code || ''];
  const scores: Record<string, number> = decision?.Model?.Scores || {};

  return (
    <div className='min-h-screen bg-[#05060A]'>
      <Navbar />
      <div className='max-w-[1920px] mx-auto px-3 sm:px-6 pt-4 pb-8'>
        <div className='flex items-center gap-3 mb-4'>
          <button onClick={() => navigate('/appeals')} className='text-xs text-gray-400 hover:text-white'>
            ← Appeals
          </button>
          <span className='font-mono text-xs text-white font-semibold'>{appeal.AppealID}</span>
          <span className='text-xs text-gray-500'>
            {appeal.UserID} · {violation?.BreachOfCommitment ? 'repeat breach' : `${violation?.Level ?? '—'} of 3`}
          </span>
        </div>

        <div className='grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-4'>
          <div className='space-y-4'>
            <Card title='What the creator says'>
              <div className='border-l-2 border-[#FF9C15]/60 pl-3'>
                <p className='text-sm text-gray-200 leading-relaxed'>
                  {appeal.Statement || <span className='text-gray-600'>No statement given.</span>}
                </p>
                <p className='text-[11px] text-gray-500 mt-2'>
                  {appeal.StatementLocale && (
                    <span className='font-mono uppercase border border-white/10 rounded px-1 py-0.5 mr-1.5'>
                      {appeal.StatementLocale}
                    </span>
                  )}
                  submitted {new Date(appeal.SubmittedAt).toLocaleString()}
                  {appeal.ReasonCode ? ` · reason: ${appeal.ReasonCode}` : ''}
                </p>
              </div>
            </Card>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <Card title='The original decision'>
                <Kv k='Reference' v={decision?.DecisionID} mono />
                <Kv k='Outcome' v={`${decision?.Action} · ${decision?.Code}`} bad />
                <Kv k='Clause' v={decision?.Clause ? `CG § ${decision.Clause}` : '—'} />
                <Kv k='Decided' v={decision?.DecidedAt ? new Date(decision.DecidedAt).toLocaleString() : '—'} />
                <Kv k='Decided by' v={neverHuman ? 'Automatic only' : decision?.ActorUserID} bad={neverHuman} />
                <Kv
                  k='Posted'
                  v={decision?.ContentCreatedAt ? new Date(decision.ContentCreatedAt).toLocaleDateString() : '—'}
                />
                <Kv k='Evidence kept' v={decision?.EvidenceKeys?.length ? `${decision.EvidenceKeys.length} files` : 'none'} bad={!decision?.EvidenceKeys?.length} />
              </Card>

              <Card title='Model evidence'>
                {Object.keys(scores).length === 0 && (
                  <p className='text-xs text-gray-500'>No model scores recorded for this decision.</p>
                )}
                {Object.entries(scores).map(([label, v]) => (
                  <div key={label} className='flex items-center gap-2.5 mb-2'>
                    <span className='text-[11px] text-gray-300 w-20 shrink-0 capitalize'>{label}</span>
                    <div className='flex-grow h-1.5 rounded bg-white/10 relative'>
                      <div
                        className='absolute left-0 top-0 bottom-0 rounded'
                        style={{ width: `${Math.round(v * 100)}%`, background: v > 0.8 ? '#F97066' : '#475467' }}
                      />
                    </div>
                    <span className='text-[11px] font-semibold text-white w-9 text-right tabular-nums'>
                      {v.toFixed(2)}
                    </span>
                  </div>
                ))}
                <div className='mt-3 pt-2 border-t border-white/10'>
                  <Kv k='Model' v={decision?.Model?.ModelId || '—'} mono />
                  <Kv k='Engine' v={decision?.Model?.Engine || '—'} mono />
                  <Kv k='Prompt' v={decision?.Model?.PromptVersion || '—'} mono />
                  <Kv k='Hash' v={decision?.ContentHash || '—'} mono />
                </div>
              </Card>
            </div>

            {decision?.Model?.RawFinding && (
              <Card title='What the model wrote at the time'>
                <p className='text-sm text-gray-200 italic'>“{decision.Model.RawFinding}”</p>
              </Card>
            )}

            <Card title='Creator history'>
              {history.length === 0 && <p className='text-xs text-gray-500'>No prior violations.</p>}
              {history.map((h: any) => (
                <div key={h.ViolationID} className='flex items-center gap-2.5 py-1.5 text-xs'>
                  <span
                    className='w-1.5 h-1.5 rounded-full shrink-0'
                    style={{
                      background:
                        h.Status === 'VACATED' ? '#6CE9A6' : h.BreachOfCommitment ? '#F97066' : '#FDB022',
                    }}
                  />
                  <span className='text-gray-300'>
                    Violation {h.Level} · {h.Code}
                    {h.BreachOfCommitment ? ' · broke commitment' : ''}
                  </span>
                  <span className='text-gray-600 ml-auto tabular-nums'>
                    {new Date(h.OpenedAt).toLocaleDateString()}
                  </span>
                  <span className='text-gray-500'>{h.Status}</span>
                </div>
              ))}
            </Card>
          </div>

          <div className='space-y-3'>
            {(neverHuman || hint) && (
              <div className='rounded-xl p-3 bg-amber-500/10 border border-amber-500/30'>
                <p className='text-xs text-amber-300 leading-relaxed'>
                  {neverHuman && <b>No person has watched this yet. You are the first. </b>}
                  {hint}
                </p>
              </div>
            )}

            <div className='text-[10px] font-bold uppercase tracking-wider text-gray-500'>Your decision</div>

            <Option
              on={outcome === 'overturn'}
              onClick={() => setOutcome('overturn')}
              title='Overturn — we got it wrong'
              desc='Restores the video, wipes the violation, refunds the 200 coins, tells the creator.'
            />
            <Option
              on={outcome === 'uphold'}
              onClick={() => setOutcome('uphold')}
              title='Uphold — removal stands'
              desc='Violation stays on record. The creator can post again once they read the outcome.'
            />
            <Option
              on={outcome === 'close'}
              onClick={() => setOutcome('close')}
              title='Uphold and close the account'
              desc={isZero ? 'Preserves evidence and files the external report.' : 'Zero-tolerance codes only.'}
              disabled={!isZero}
            />

            <div>
              <div className='text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5'>
                Why (the creator sees this)
              </div>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                placeholder='One or two sentences. This becomes the statement of reasons.'
                className='w-full rounded-lg bg-white/[0.03] border border-white/[0.13] p-2.5 text-xs text-white placeholder-gray-600 outline-none focus:border-[#FF9C15]/50'
              />
            </div>

            <button
              disabled={saving}
              onClick={decide}
              className='w-full h-10 rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white text-sm font-semibold disabled:opacity-50'
            >
              {saving ? 'Saving…' : 'Decide and notify'}
            </button>
            <p className='text-[10px] text-gray-600 text-center leading-relaxed'>
              Recorded against you with a timestamp and cannot be edited afterwards. You cannot decide an
              appeal against a decision you made.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const Card: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className='bg-white/[0.045] border border-white/10 rounded-xl p-3.5'>
    <div className='text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2.5'>{title}</div>
    {children}
  </div>
);

const Kv: React.FC<{ k: string; v?: string; mono?: boolean; bad?: boolean }> = ({ k, v, mono, bad }) => (
  <div className='flex justify-between items-center py-1.5 border-t border-white/[0.06] first:border-t-0 first:pt-0 gap-3'>
    <span className='text-[11px] text-gray-400 shrink-0'>{k}</span>
    <span
      className={`text-[11px] font-semibold text-right truncate ${bad ? 'text-red-300' : 'text-white'} ${
        mono ? 'font-mono text-[10.5px]' : ''
      }`}
    >
      {v || '—'}
    </span>
  </div>
);

const Option: React.FC<{
  on: boolean;
  onClick: () => void;
  title: string;
  desc: string;
  disabled?: boolean;
}> = ({ on, onClick, title, desc, disabled }) => (
  <button
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    className={`w-full text-left rounded-xl border p-3 flex gap-2.5 items-start transition ${
      on ? 'border-emerald-500/55 bg-emerald-500/[0.09]' : 'border-white/10 bg-white/[0.04]'
    } ${disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white/[0.07]'}`}
  >
    <span
      className={`w-[18px] h-[18px] rounded-full shrink-0 mt-0.5 box-border ${
        on ? 'border-[5px] border-emerald-400' : 'border-[1.5px] border-white/30'
      }`}
    />
    <span>
      <span className='block text-[13px] font-semibold text-white'>{title}</span>
      <span className='block text-[11px] text-gray-400 mt-1 leading-relaxed'>{desc}</span>
    </span>
  </button>
);

export default AppealDetail;
